'use client';

import { Mail, Lock, LogIn, Eye, EyeOff } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  parentLoginSchema,
  ParentLoginInput,
} from '@/schemas/parent-auth.schema';
import { useState } from 'react';

interface LoginFormProps {
  onSubmit: (data: ParentLoginInput) => void;
  isLoading: boolean;
  errorMessage: string | null;
}

export function LoginForm({
  onSubmit,
  isLoading,
  errorMessage,
}: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ParentLoginInput>({
    resolver: zodResolver(parentLoginSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-5 sm:space-y-6"
      noValidate
    >
      {/* Email */}
      <div>
        <label
          htmlFor="email"
          className="block text-xs sm:text-sm font-medium text-gray-700"
        >
          Email address
        </label>
        <div className="relative mt-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Mail
              className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
          <input
            id="email"
            {...register('email')}
            type="email"
            autoComplete="email"
            disabled={isLoading}
            className={`block w-full rounded-md pl-9 sm:pl-10 shadow-sm focus:border-primary focus:ring-primary text-xs sm:text-sm py-2 sm:py-2.5 ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="you@example.com"
          />
        </div>
        {errors.email && (
          <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <label
          htmlFor="password"
          className="block text-xs sm:text-sm font-medium text-gray-700"
        >
          Password
        </label>
        <div className="relative mt-1">
          <Lock
            className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
            aria-hidden="true"
          />
          <input
            id="password"
            {...register('password')}
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            disabled={isLoading}
            className={`block w-full rounded-md pl-10 pr-10 shadow-sm focus:border-primary focus:ring-primary text-xs sm:text-sm py-2 sm:py-2.5 ${
              errors.password ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="••••••••"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4" aria-hidden="true" />
            ) : (
              <Eye className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
        </div>
        {errors.password && (
          <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
        )}
      </div>

      {/* Server error */}
      {errorMessage && (
        <div className="rounded-md bg-red-50 p-2 sm:p-3">
          <p className="text-xs sm:text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Submit */}
      <div>
        <button
          type="submit"
          disabled={isLoading}
          className="flex w-full justify-center rounded-md border border-transparent bg-[#0D373D] py-2 sm:py-2.5 px-3 sm:px-4 text-xs sm:text-sm font-medium text-white shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
        >
          {isLoading ? (
            <svg
              className="animate-spin -ml-1 mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <LogIn
              className="-ml-1 mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5"
              aria-hidden="true"
            />
          )}
          Sign in
        </button>
      </div>
    </form>
  );
}
